import {
    Box,
    Card,
    Divider,
    Heading,
    Pagination,
    QuickFilterButton,
    TabMenu,
    TabMenuItem,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ERASURE_CERTIFICATES_PAGE_SIZE, ErasureCertificateAsset } from "./erasureCertificates";
import {
    ALL_LOCK_TYPES,
    LOCK_PATHS,
    LOCKED_DEVICES_PAGE_SIZE,
    LockedDeviceAsset,
    LockTypeFilter,
} from "./lockedDevices";
import style from "./risk-factors-card.scss";
import { RiskFactorDataType, RiskFactorsTable } from "./risk-factors-table/RiskFactorsTable";
import { createTranslatorPath } from "services/pathTranslator";

const MAX_PAGE_NUMBERS_TO_SHOW = 3;

export interface LockedDevicesData {
    assets: LockedDeviceAsset[];
    lockTypeFilter: LockTypeFilter;
    currentPage: number;
    totalPages: number;
    totalAssets: number;
    isLoading: boolean;
}

export interface ErasureCertificatesData {
    assets: ErasureCertificateAsset[];
    currentPage: number;
    totalPages: number;
    totalAssets: number;
    isLoading: boolean;
}

interface Tab {
    dataType: RiskFactorDataType;
    label: string;
    filters?: Filter[];
}

interface Filter {
    label: string;
    isActive: boolean;
    onSelect: () => void;
}

interface PaginationState {
    currentPage: number;
    totalPages: number;
    totalAssets: number;
    rowCount: number;
    pageSize: number;
}

interface Props {
    lockedDevices: LockedDevicesData;
    erasureCertificates: ErasureCertificatesData;
    onLockTypeFilterSelect: (lockTypeFilter: LockTypeFilter) => void;
    onLockedDevicesPageChange: (page: number) => void;
    onErasureCertificatesPageChange: (page: number) => void;
    onErasureReportUploaded: () => void;
}

export const RiskFactorsCard = (props: Props) => {
    const { t } = useTranslation();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");

    const [activeDataType, setActiveDataType] = useState<RiskFactorDataType>("LOCKED_DEVICES");

    const isLockedDevicesTab = activeDataType === "LOCKED_DEVICES";
    const lockTypeFilter = props.lockedDevices.lockTypeFilter;

    const tabs: Tab[] = [
        {
            dataType: "ERASURE_CERTIFICATE",
            label: t("AssetManagement.risk.riskOverviewTable.erasureCertificate", {
                count: props.erasureCertificates.totalAssets,
            }),
            filters: [
                {
                    label: t("Common.all"),
                    isActive: true,
                    onSelect: () => {},
                },
            ],
        },
        {
            dataType: "LOCKED_DEVICES",
            label: t("AssetManagement.risk.riskOverviewTable.lockedDevices", {
                count: props.lockedDevices.totalAssets,
            }),
            filters: [
                {
                    label: t("Common.all"),
                    isActive: lockTypeFilter === ALL_LOCK_TYPES,
                    onSelect: () => props.onLockTypeFilterSelect(ALL_LOCK_TYPES),
                },
                ...LOCK_PATHS.map((lockPath) => ({
                    label: translatedPaths(lockPath),
                    isActive: lockTypeFilter === lockPath,
                    onSelect: () => props.onLockTypeFilterSelect(lockPath),
                })),
            ],
        },
    ];

    const activeTab = tabs.find((tab) => tab.dataType === activeDataType) ?? tabs[0];

    const pagination: PaginationState = isLockedDevicesTab
        ? {
              currentPage: props.lockedDevices.currentPage,
              totalPages: props.lockedDevices.totalPages,
              totalAssets: props.lockedDevices.totalAssets,
              rowCount: props.lockedDevices.assets.length,
              pageSize: LOCKED_DEVICES_PAGE_SIZE,
          }
        : {
              currentPage: props.erasureCertificates.currentPage,
              totalPages: props.erasureCertificates.totalPages,
              totalAssets: props.erasureCertificates.totalAssets,
              rowCount: props.erasureCertificates.assets.length,
              pageSize: ERASURE_CERTIFICATES_PAGE_SIZE,
          };

    return (
        <Card padding={"None"} className={style.card}>
            <Box paddingTop={"Sm"} paddingLeft={"Sm"} paddingRight={"Sm"} paddingBottom={"Xxs"}>
                <Heading variant="h2">{t("AssetManagement.risk.riskOverviewTable.heading")}</Heading>
            </Box>

            <Divider />

            <Box columnLayout paddingTop={"Xxs"} paddingLeft={"Sm"} paddingRight={"Sm"} paddingBottom={"Sm"} gap={"Sm"}>
                <TabMenu>
                    {tabs.map((tab, index) => (
                        <TabMenuItem
                            key={index}
                            isActive={activeDataType === tab.dataType}
                            onClick={() => setActiveDataType(tab.dataType)}
                            dataType={tab.dataType}
                        >
                            {tab.label}
                        </TabMenuItem>
                    ))}
                </TabMenu>

                {activeTab.filters !== undefined && (
                    <Box flexWrap="Wrap" gap={"Xxs"}>
                        {activeTab.filters.map((filter) => (
                            <QuickFilterButton key={filter.label} isActive={filter.isActive} onClick={filter.onSelect}>
                                {filter.label}
                            </QuickFilterButton>
                        ))}
                    </Box>
                )}

                <Box className={style.tableContainer}>
                    <RiskFactorsTable
                        dataType={activeDataType}
                        erasureCertificates={props.erasureCertificates.assets}
                        lockedDevices={props.lockedDevices.assets}
                        isLoading={
                            isLockedDevicesTab ? props.lockedDevices.isLoading : props.erasureCertificates.isLoading
                        }
                        onErasureReportUploaded={props.onErasureReportUploaded}
                    />
                </Box>

                <Box
                    horizontalAlignment={"Right"}
                    className={classNames({ [style.hiddenPagination]: pagination.totalAssets === 0 })}
                >
                    <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        maxPageNumbersToShow={MAX_PAGE_NUMBERS_TO_SHOW}
                        handlePageChange={(page) => {
                            if (isLockedDevicesTab) {
                                props.onLockedDevicesPageChange(page);
                                return;
                            }
                            props.onErasureCertificatesPageChange(page);
                        }}
                        paginationText={t("AssetManagement.showingAssets", {
                            start: (pagination.currentPage - 1) * pagination.pageSize + 1,
                            end: (pagination.currentPage - 1) * pagination.pageSize + pagination.rowCount,
                            total: pagination.totalAssets,
                        })}
                    ></Pagination>
                </Box>
            </Box>
        </Card>
    );
};
